import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react';
import Translate from 'ui/atoms/translate';
import Button from 'ui/atoms/button';
import ServerError from 'ui/types/server-error';
import Section from 'ui/atoms/section';
import Hint from 'ui/atoms/hint';
import { WalletType } from 'ui/types/wallet';
import makeForm from 'ui/forms';
import * as Styled from './styled';
import Header from 'ui/atoms/header';
import Spacer from 'src/ui/atoms/spacer';
import { InvestorsApi } from 'src/api';
import useInvestorMe from 'src/hooks/use-investor-me';
import useApiCall from 'src/hooks/use-api-call';
import WizardContext from 'src/libraries/wizard/wizard-context';

export interface TFADevice {
  confirmed: boolean;
  walletNeeded: boolean;
}

export interface InvestmentSelectDepotProps {
  /** Additional classes. */
  className?: string;

  loading?: boolean;

  error?: ServerError;

  onDepotSelected?: (selectedWallet: WalletType[]) => void;

  prioritizedAllowedWalletTypes: WalletType[];

  tfaDevice?: TFADevice;
}

const SelectDepotForm = makeForm<{
  walletOption: WalletType | '';
  isCashlinkEthereumSelected: boolean;
  isCashlinkStellarSelected: boolean;
}>();

const InvestmentSelectDepot: FunctionComponent<InvestmentSelectDepotProps> = ({
  className,
  loading,
  error,
  onDepotSelected = () => {},
  prioritizedAllowedWalletTypes,
  tfaDevice,
}) => {
  const { makeAuthenticatedApi, withApi } = useApiCall();

  const { investor } = useInvestorMe();

  const { finalize } = useContext(WizardContext);

  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);

  const needMobileAppWalletUpdate = prioritizedAllowedWalletTypes.includes(WalletType.MOBILE) && tfaDevice?.confirmed;

  const skipWalletSelection = useCallback(() => {
    if (!investor?.id) return;

    withApi(async () => {
      await investorsApi.investorsSkipWalletSelectionCreate({
        id: investor.id,
      });

      finalize();
    });
  }, [withApi, investor?.id]);

  return (
    <div className={className}>
      <SelectDepotForm
        initial={{ walletOption: '' }}
        onSubmit={({ walletOption, isCashlinkEthereumSelected, isCashlinkStellarSelected }) => {
          if (walletOption === '') {
            skipWalletSelection();
          } else {
            onDepotSelected([
              ...(walletOption === WalletType.CASHLINK ? [] : [walletOption]),
              ...((walletOption === WalletType.CASHLINK && isCashlinkEthereumSelected) ||
              (prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKETHEREUM) &&
                walletOption === WalletType.CASHLINK &&
                !prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKSTELLAR))
                ? [WalletType.CASHLINKETHEREUM]
                : []),
              ...((walletOption === WalletType.CASHLINK && isCashlinkStellarSelected) ||
              (prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKSTELLAR) &&
                walletOption === WalletType.CASHLINK &&
                !prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKETHEREUM))
                ? [WalletType.CASHLINKSTELLAR]
                : []),
            ]);
          }
        }}
        error={error}
      >
        <p>
          <Translate name="investmentDepot.walletGuide.description" />
        </p>
        <Header size="small" spacing="medium">
          <Translate name="investmentDepot.walletGuide.explanationTitle" />
        </Header>
        <p>
          <Translate name="investmentDepot.walletGuide.explanation" />
        </p>
        {needMobileAppWalletUpdate && (
          <Hint variant="warning">
            <Translate name="investmentDepot.selection.needMobileAppWalletUpdate" />
          </Hint>
        )}
        <Spacer y={6} />
        <Header size="small" spacing="medium">
          <Translate name="investmentDepot.selection.title" />
        </Header>
        <Section spacing="small">
          <SelectDepotForm.ValueProvider>
            {({ isCashlinkEthereumSelected, isCashlinkStellarSelected, walletOption }) => (
              <>
                <SelectDepotForm.Group name="walletOption">
                  <SelectDepotForm.Radio key={0} value={''}>
                    <Translate name="wallet.skip" />
                  </SelectDepotForm.Radio>
                </SelectDepotForm.Group>
                <SelectDepotForm.Group name="walletOption">
                  {(prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKETHEREUM) ||
                    prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKSTELLAR)) && (
                    <SelectDepotForm.Radio value={WalletType.CASHLINK}>
                      <Translate name={'investmentDepot.selection.new.cashlinkEthereum'} />
                      <Styled.WalletSelectDetails>
                        <Translate
                          name={`investmentDepot.selection.details.${
                            prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKETHEREUM) &&
                            prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKSTELLAR)
                              ? 'cashlink'
                              : prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKETHEREUM)
                              ? 'cashlinkEthereum'
                              : 'cashlinkStellar'
                          }`}
                        />
                      </Styled.WalletSelectDetails>
                      {prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKETHEREUM) &&
                        prioritizedAllowedWalletTypes.includes(WalletType.CASHLINKSTELLAR) &&
                        walletOption === WalletType.CASHLINK && (
                          <>
                            <SelectDepotForm.Group
                              name="isCashlinkEthereumSelected"
                              label={null}
                              spacing="small"
                              required={!isCashlinkStellarSelected}
                              requiredError={<Translate name="investmentDepot.selection.networkError" />}
                            >
                              <SelectDepotForm.Checkbox>
                                <Translate name="investmentDepot.selection.etheremAndPolygon" />
                              </SelectDepotForm.Checkbox>
                            </SelectDepotForm.Group>
                            <SelectDepotForm.Group
                              name="isCashlinkStellarSelected"
                              label={null}
                              required={!isCashlinkEthereumSelected}
                              requiredError={<Translate name="investmentDepot.selection.networkError" />}
                            >
                              <SelectDepotForm.Checkbox>
                                <Translate name="investmentDepot.selection.stellar" />
                              </SelectDepotForm.Checkbox>
                            </SelectDepotForm.Group>
                          </>
                        )}
                    </SelectDepotForm.Radio>
                  )}
                  {prioritizedAllowedWalletTypes.includes(WalletType.TANGANY) && (
                    <SelectDepotForm.Radio value={WalletType.TANGANY}>
                      <Translate name={'investmentDepot.selection.new.tangany'} />
                      <Styled.WalletSelectDetails>
                        <Translate name={'investmentDepot.selection.details.tangany'} />
                      </Styled.WalletSelectDetails>
                    </SelectDepotForm.Radio>
                  )}
                  {prioritizedAllowedWalletTypes.includes(WalletType.MOBILE) && (
                    <SelectDepotForm.Radio value={WalletType.MOBILE}>
                      <Translate name={'investmentDepot.selection.new.mobile'} />
                      <Styled.WalletSelectDetails>
                        <Translate name={'investmentDepot.selection.details.mobile'} />
                      </Styled.WalletSelectDetails>
                    </SelectDepotForm.Radio>
                  )}
                </SelectDepotForm.Group>
              </>
            )}
          </SelectDepotForm.ValueProvider>
        </Section>
        <Section spacing="small">
          {prioritizedAllowedWalletTypes.includes(WalletType.GENERIC) && (
            <Button variant="link" onClick={() => onDepotSelected([WalletType.GENERIC])}>
              <Translate name="investmentDepot.selection.new.generic" />
            </Button>
          )}
        </Section>
        <SelectDepotForm.Submit loading={loading} size="large" fluid={true} variant="primary">
          <Translate name="common.continue" />
        </SelectDepotForm.Submit>
      </SelectDepotForm>
    </div>
  );
};

export default InvestmentSelectDepot;
